import {
  DashboardDeviceType,
  Dashboard as DashboardSchema,
  TaskListType,
} from '@akin-home/amplify';
import { HasID } from '@akin-home/records';
import { colors } from '@akin-home/theme';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export enum DashboardTarget {
  mobile_landscape = 'mobile_landscape',
  mobile_portrait = 'mobile_portrait',
  tablet_landscape = 'tablet_landscape',
  tablet_portrait = 'tablet_portrait',
}

/** User can have different dashboards depending on the platform */
export enum DashboardPlatform {
  MOBILE = 'mobile',
  TABLET = 'tablet',
}

/** Possible widget types */
export enum DashboardWidgetType {
  PROFILE = 'profile',
  STICKY_NOTE = 'sticky_notes',
  CONTACTS = 'contacts',
  GOAL_LIST = 'goal_list',
  GOAL_CAROUSEL = 'goal_carousel',
  NEXT_EVENT = 'next_event',
  NEXT_MEAL = 'next_meal',
  MEALS = 'meals',
  CHECKLISTS = 'checklists',
  TODAY_SCHEDULE = 'today_schedule',
  TIMER = 'timer',
  IMAGE = 'image',
  GOAL_DETAILS = 'goal_details',
  APP = 'app',
  PHONE = 'phone',
  CAMERA = 'camera',
  WEATHER = 'weather',
}

/** This can be used to generate a dashboard for a user. */
export interface DashboardTemplate extends HasID {
  name?: string;
  description?: string;
  device_type: DashboardDeviceType;
  layout?: DashboardWidgetUnion[];
}

export interface DashboardModel extends Omit<DashboardSchema, 'layout'> {
  id: string;
  name?: string;
  description?: string;

  device_type?: DashboardDeviceType;
  user_id?: string;

  layout?: DashboardWidgetUnion[];
}

export type DashboardLayout = DashboardWidgetUnion[][];

type OneToTwelve = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

/** Zero Indexed Position */
type ZeroToEleven = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export interface DashboardWidgetPosition {
  width: OneToTwelve;
  height: OneToTwelve;
  top: ZeroToEleven;
  left: ZeroToEleven;
}

export interface DashboardWidgetBase {
  id: string;

  positions: {
    landscape?: DashboardWidgetPosition;
    portrait?: DashboardWidgetPosition;
  };
}

export interface DashboardWidgetProfile extends DashboardWidgetBase {
  type: DashboardWidgetType.PROFILE;
}

export interface DashboardWidgetStickyNotes extends DashboardWidgetBase {
  type: DashboardWidgetType.STICKY_NOTE;
}

export interface DashboardWidgetContacts extends DashboardWidgetBase {
  type: DashboardWidgetType.CONTACTS;
}

export interface DashboardWidgetChecklists extends DashboardWidgetBase {
  type: DashboardWidgetType.CHECKLISTS;

  data?: {
    types?: TaskListType[];
  };
}

export interface DashboardWidgetNextEvent extends DashboardWidgetBase {
  type: DashboardWidgetType.NEXT_EVENT;
}

export interface DashboardWidgetNextMeal extends DashboardWidgetBase {
  type: DashboardWidgetType.NEXT_MEAL;
}

export interface DashboardWidgetMeals extends DashboardWidgetBase {
  type: DashboardWidgetType.MEALS;
}

export interface DashboardWidgetGoalList extends DashboardWidgetBase {
  type: DashboardWidgetType.GOAL_LIST;
}

export interface DashboardWidgetGoalCarousel extends DashboardWidgetBase {
  type: DashboardWidgetType.GOAL_CAROUSEL;
}

export interface DashboardWidgetGoalDetails extends DashboardWidgetBase {
  type: DashboardWidgetType.GOAL_DETAILS;
}

export interface DashboardWidgetTodaySchedule extends DashboardWidgetBase {
  type: DashboardWidgetType.TODAY_SCHEDULE;
}

export interface DashboardWidgetTimer extends DashboardWidgetBase {
  type: DashboardWidgetType.TIMER;

  data?: {
    /** The sound that will be played when the timer ends */
    sound_id?: string;
  };
}

export interface DashboardWidgetImage extends DashboardWidgetBase {
  type: DashboardWidgetType.IMAGE;

  data?: {
    /** Images to be displayed */
    images?: {
      /** A url for the image to be displayed */
      image_url?: string;
      /** ID of the image to be displayed */
      image_id?: string;
    }[];

    /** TODO, add some more attributes around cycling of images etc */
  };
}

export interface DashboardWidgetPhone extends DashboardWidgetBase {
  type: DashboardWidgetType.PHONE;
}

export interface DashboardWidgetCamera extends DashboardWidgetBase {
  type: DashboardWidgetType.CAMERA;
}

export interface DashboardWidgetApp extends DashboardWidgetBase {
  type: DashboardWidgetType.APP;

  data?: {
    /** The package name of the linked app */
    package_name?: string;
  };
}

export interface DashboardWidgetWeather extends DashboardWidgetBase {
  type: DashboardWidgetType.WEATHER;
}

/** Union of all possible widgets */
export type DashboardWidgetUnion =
  | DashboardWidgetProfile
  | DashboardWidgetStickyNotes
  | DashboardWidgetContacts
  | DashboardWidgetNextEvent
  | DashboardWidgetNextMeal
  | DashboardWidgetChecklists
  | DashboardWidgetGoalList
  | DashboardWidgetGoalCarousel
  | DashboardWidgetGoalDetails
  | DashboardWidgetTodaySchedule
  | DashboardWidgetTimer
  | DashboardWidgetImage
  | DashboardWidgetApp
  | DashboardWidgetPhone
  | DashboardWidgetCamera
  | DashboardWidgetMeals
  | DashboardWidgetWeather;

export interface DashboardWidgetDetailType {
  type: DashboardWidgetType;
  title: string;
  description: string;
  required?: boolean;
  color: string;
  icon_color: string;
  icon: IconProp;
  video_url?: string;

  /** Can a user select this for themselves from the dashboard screen. */
  user_selectable: boolean;
}

export const DASHBOARD_WIDGET_DETAILS: DashboardWidgetDetailType[] = [
  {
    type: DashboardWidgetType.TODAY_SCHEDULE,
    title: 'Calendar',
    description: ' Stay on top of your schedule with the Hey Pixi Calendar.',
    required: true,
    color: '#FFEBE3',
    icon_color: colors.orange.crayola,
    icon: 'calendar-day',
    user_selectable: true,
  },
  {
    type: DashboardWidgetType.CONTACTS,
    title: 'Contacts',
    description:
      'Stay connected with your loved ones and store all your important numbers.',
    color: '#FBEEF7',
    icon_color: colors.pink.primary,
    icon: 'user-friends',
    user_selectable: true,
  },
  {
    type: DashboardWidgetType.STICKY_NOTE,
    title: 'Notes',
    description:
      'A place to jot down pet name ideas or that million-dollar business idea.',
    color: '#FFF6DA',
    icon_color: colors.yellow[700],
    icon: 'sticky-note',
    user_selectable: true,
  },
  {
    type: DashboardWidgetType.GOAL_CAROUSEL,
    title: 'Goals',
    description:
      'For goals big and small – track your progress and stay motivated.',
    color: '#DCF1FF',
    icon_color: colors.secondary[700],
    icon: 'trophy-star',
    user_selectable: true,
  },
  {
    type: DashboardWidgetType.MEALS,
    title: 'Meals',
    description: 'Plan delicious meals each week.',
    color: colors.orange.background,
    icon_color: colors.orange.crayola,
    icon: 'salad',
    user_selectable: true,
  },
  {
    type: DashboardWidgetType.CHECKLISTS,
    title: 'Lists',
    description: 'From groceries lists, to daily reminders.',
    color: '#E5E6FD',
    icon_color: colors.primary[700],
    icon: 'tasks',
    user_selectable: true,
  },
  {
    type: DashboardWidgetType.TIMER,
    title: 'Timer',
    description: 'Set a timer right from your home screen.',
    color: '#FFF6DA',
    icon_color: colors.yellow[700],
    icon: 'timer',
    user_selectable: false,
  },
  {
    type: DashboardWidgetType.WEATHER,
    title: 'Weather',
    description: 'See the weather forcast on your home screen.',
    color: colors.green.todo_list_background,
    icon_color: colors.green[700],
    icon: 'sun-cloud',
    user_selectable: true,
  },
];
